/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { i18n } from '@lingui/core'
import { TabContext, TabPanel } from '@mui/lab'
import {
  Box,
  FormControl,
  MenuItem,
  NoSsr,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  styled,
} from '@mui/material'
import { useEffect, useState } from 'react'
import useCallAPI from '../../hooks/useCallAPI'
import { SecondaryButton } from '../Button'
import { ReviewItem } from './ReviewItem'

export type ReviewItemTabsProps = {
  reviewsCount: number | null | undefined
  productId: number | null | undefined
  customer: any | undefined
}

interface StyledTabsProps {
  children?: React.ReactNode
  value: string
  onChange: (event: React.SyntheticEvent, newValue: string) => void
  variant?: 'standard' | 'scrollable' | 'fullWidth' | undefined
  scrollButtons?: boolean
  allowScrollButtonsMobile?: boolean
}

export const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs {...props} TabIndicatorProps={{ children: <span className='MuiTabs-indicatorSpan' /> }} />
))({
  minHeight: 35,
  height: 35,
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    height: 2,
  },
  '& .MuiTabs-indicatorSpan': {
    width: '100%',
    backgroundColor: '#A95CFF',
  },
})

interface StyledTabProps {
  label: string
  id: string
  value: string
  'aria-controls': string
}

export const StyledTab = styled((props: StyledTabProps) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    minWidth: 0,
    minHeight: 35,
    height: 35,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 16,
    color: '#6A6A6A',
    ':hover': {
      color: '#000000',
    },
    '&.Mui-selected': {
      fontWeight: 'bold',
      color: '#A95CFF',
      borderBottom: '2px solid #A95CFF',
      ':hover': {
        color: '#613394',
        borderColor: '#613394',
      },
    },
    '&.Mui-focusVisible': {},
  }),
)

interface StyledTabPanelProps {
  value: string
  children?: React.ReactNode
}

export const StyledTabPanel = styled((props: StyledTabPanelProps) => <TabPanel {...props} />)(
  ({ theme }) => ({
    '&.MuiTabPanel-root': {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 12,
      paddingBottom: 12,
    },
  }),
)

export function ReviewItemTabs(props: ReviewItemTabsProps) {
  const { productId, customer, reviewsCount } = props
  const [value, setValue] = useState('0')
  const [reviews, setReviews] = useState<any>([])
  const [currentPage, setCurrentPage] = useState<any>(1)
  const [hasMoreReviews, setHasMoreReviews] = useState(true)

  const reviewsPerPage = 5

  const a11yProps = (index: number) => ({
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  })

  const { apiCall: reviewsDataSortByDateDescApiCall, loading: sortByDateLoading } = useCallAPI({
    url: `${process.env.NEXT_PUBLIC_YOTPO_API_URL}/v1/widget/${process.env.NEXT_PUBLIC_YOTPO_APP_KEY}/products/${productId}/reviews.json?per_page=${reviewsPerPage}&page=${currentPage}&sort=date&direction=desc`,
    contentType: 'application/json',
    method: 'GET',
  })

  const { apiCall: reviewsDataSortByRatingDescApiCall, loading: sortByRatingsDescLoading } =
    useCallAPI({
      url: `${process.env.NEXT_PUBLIC_YOTPO_API_URL}/v1/widget/${process.env.NEXT_PUBLIC_YOTPO_APP_KEY}/products/${productId}/reviews.json?per_page=${reviewsPerPage}&page=${currentPage}&sort=rating&direction=desc`,
      contentType: 'application/json',
      method: 'GET',
    })

  const { apiCall: reviewsDataSortByRatingAscApiCall, loading: sortByRatingsAscLoading } =
    useCallAPI({
      url: `${process.env.NEXT_PUBLIC_YOTPO_API_URL}/v1/widget/${process.env.NEXT_PUBLIC_YOTPO_APP_KEY}/products/${productId}/reviews.json?per_page=${reviewsPerPage}&page=${currentPage}&sort=rating&direction=asc`,
      contentType: 'application/json',
      method: 'GET',
    })

  const { apiCall: reviewsDataSortByVotesDescApiCall, loading: sortByVotesLoading } = useCallAPI({
    url: `${process.env.NEXT_PUBLIC_YOTPO_API_URL}/v1/widget/${process.env.NEXT_PUBLIC_YOTPO_APP_KEY}/products/${productId}/reviews.json?per_page=${reviewsPerPage}&page=${currentPage}&sort=votes_up&direction=desc`,
    contentType: 'application/json',
    method: 'GET',
  })

  useEffect(() => {
    const fetchBottomLineData = async () => {
      let res
      switch (value) {
        case '0':
          res = await reviewsDataSortByDateDescApiCall()
          break
        case '1':
          res = await reviewsDataSortByRatingDescApiCall()
          break
        case '2':
          res = await reviewsDataSortByRatingAscApiCall()
          break
        case '3':
          res = await reviewsDataSortByVotesDescApiCall()
          break
        default:
          res = await reviewsDataSortByDateDescApiCall()
          break
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const reviewsArr = [...reviews]
      // eslint-disable-next-line no-unsafe-optional-chaining
      reviewsArr.push(...res?.data?.response?.reviews)
      if (reviewsArr?.length >= res?.data?.response.bottomline?.total_review) {
        setHasMoreReviews(false)
      }
      setReviews(reviewsArr)
    }

    fetchBottomLineData().catch((e) => {
      // addtional error handling here, the error must be set by the useCallAPI hook already
      console.error(e)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  const handleLoadNextPage = () => {
    // eslint-disable-next-line radix
    setCurrentPage(Number.parseInt(currentPage) + 1)
  }

  const handleChange = (event: SelectChangeEvent<string>, newValue: string) => {
    setReviews([])
    setHasMoreReviews(true)
    // eslint-disable-next-line no-new-wrappers
    setCurrentPage(new Number(1))
    setValue(newValue)
  }

  return (
    <TabContext value={value}>
      <Box className='flex w-full items-center justify-end'>
        <FormControl size='small' className='min-w-[150px]'>
          <Select
            value={value}
            onChange={(e) => handleChange(e, e.target.value)}
            displayEmpty
            inputProps={{ 'aria-label': 'Review Sort Options' }}
            MenuProps={{
              PaperProps: {
                sx: {
                  width: '150px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                },
              },
            }}
          >
            <MenuItem value='0'>Newest</MenuItem>
            <MenuItem value='1'>Best</MenuItem>
            <MenuItem value='2'>Worst</MenuItem>
            <MenuItem value='3'>Most Helpful</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* PANELS REMAIN THE SAME */}
      <StyledTabPanel value='0'>
        <div className='flex flex-col gap-[20px]'>
          <div>
            {reviews?.map((review) => (
              <NoSsr>
                <ReviewItem {...review} customer={customer} />
              </NoSsr>
            ))}
          </div>
        </div>
      </StyledTabPanel>

      <StyledTabPanel value='1'>
        <div className='flex flex-col gap-[20px]'>
          <span className='Type-XXL-Regular lg:inline lg:pb-0'>
            Reviews <div className='Type-XXL-Regular right-0 inline pl-4'>({reviewsCount})</div>
          </span>
          <div>
            {reviews?.map((review) => (
              <NoSsr>
                <ReviewItem {...review} customer={customer} />
              </NoSsr>
            ))}
          </div>
        </div>
      </StyledTabPanel>

      <StyledTabPanel value='2'>
        <div className='flex flex-col gap-[20px]'>
          <span className='Type-XXL-Regular lg:inline lg:pb-0'>
            Reviews <div className='Type-XXL-Regular right-0 inline pl-4'>({reviewsCount})</div>
          </span>
          <div>
            {reviews?.map((review) => (
              <NoSsr>
                <ReviewItem {...review} customer={customer} />
              </NoSsr>
            ))}
          </div>
        </div>
      </StyledTabPanel>

      <StyledTabPanel value='3'>
        <div className='flex flex-col gap-[20px]'>
          <span className='Type-XXL-Regular lg:inline lg:pb-0'>
            Reviews <div className='Type-XXL-Regular right-0 inline pl-4'>({reviewsCount})</div>
          </span>
          <div>
            {reviews?.map((review) => (
              <NoSsr>
                <ReviewItem {...review} customer={customer} />
              </NoSsr>
            ))}
          </div>
        </div>
      </StyledTabPanel>

      {hasMoreReviews && (
        <SecondaryButton
          className='w-full border-bright-templi-purple text-bright-templi-purple lg:w-[250px]'
          onClick={handleLoadNextPage}
          loading={
            sortByDateLoading ||
            sortByRatingsAscLoading ||
            sortByRatingsDescLoading ||
            sortByVotesLoading
          }
        >
          {i18n._('Read more reviews...')}
        </SecondaryButton>
      )}
    </TabContext>
  )
}
